import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import useNewBslAccountAPI from "../../api/bslAccountAPI";
import BalanceCard from "../../Components/BankAccountOverview/BalanceCard";
import BankDetailsCard from "../../Components/BankAccountOverview/BankDetailsCard";
import PendingApprovalState, {
  BSL_APPLY_IBAN_STATE,
  BSL_PENDING_APPROVAL_STATE,
} from "../../Components/Common/PendingApprovalState";
import ApplyForIbanButton from "../../Components/AccountSummary/ApplyForIbanButton";
import { BSL_STATUS } from "../../Components/Sidebar/utils/sidebarStorage";
import "../../Styles/pages/bank-account-overview.scss";

const FALLBACK_CURRENCIES = ["USD", "LBP"];

function normalizeCurrencyCode(row, fallbackCurrency) {
  const directValue =
    row?.Currency ||
    row?.CurrencyCode ||
    row?.CurrencyName ||
    row?.CurrencyBalances?.Currency ||
    row?.CurrencyBankDetails?.Currency;

  const currencyId = Number(row?.CurrencyID);
  if (currencyId === 1) return "USD";
  if (currencyId === 2) return "LBP";
  if (currencyId === 178) return "EUR";

  const normalized = String(directValue || fallbackCurrency || "")
    .trim()
    .toUpperCase();

  if (normalized === "USD" || normalized === "$") return "USD";
  if (normalized === "LBP" || normalized === "L.L" || normalized === "LL") return "LBP";
  if (["EUR", "EURO", "€"].includes(normalized)) return "EUR";

  return fallbackCurrency;
}

function normalizeBslStatus(status) {
  if (status === null || status === undefined || status === "") return null;
  const numericStatus = Number(status);
  return Number.isFinite(numericStatus) ? numericStatus : null;
}

function buildAccounts(rows) {
  const source = Array.isArray(rows) ? rows : [];

  const currencies = source.some((row) => normalizeCurrencyCode(row) === "EUR")
    ? [...FALLBACK_CURRENCIES, "EUR"]
    : FALLBACK_CURRENCIES;

  return currencies.map((currency) => {
    const matchedRow =
      source.find((row) => normalizeCurrencyCode(row) === currency) || {};
    const balances = matchedRow?.CurrencyBalances || {};
    const details = matchedRow?.CurrencyBankDetails || {};

    return {
      currency,
      title: `BSL BANK ${currency} ACCOUNT`,
      total: balances?.Available ?? 0,
      available: balances?.Available ?? 0,
      book: balances?.Book ?? 0,
      held: balances?.Held ?? 0,
      connectionState: matchedRow?.State,
      lastBalanceDate: matchedRow?.LastBalanceDate,
      iban: details?.IBAN || "",
      bicSwift: details?.BIC || "",
    };
  });
}

export default function BankAccountOverviewPage() {
  const { GetBankAccountOverview, GetBankAccountStatus, GetIBANCertificate } =
    useNewBslAccountAPI();
  const getOverviewRef = useRef(GetBankAccountOverview);
  const getStatusRef = useRef(GetBankAccountStatus);
  const getCertificateRef = useRef(GetIBANCertificate);
  const bankOverview = useSelector(
    (state) => state?.app?.tables?.bslAccountOverview?.data || [],
  );
  const bankAccountStatus = useSelector(
    (state) => state?.app?.summary?.bankAccountStatus ?? null,
  );

  getOverviewRef.current = GetBankAccountOverview;
  getStatusRef.current = GetBankAccountStatus;
  getCertificateRef.current = GetIBANCertificate;

  useEffect(() => {
    Promise.allSettled([getOverviewRef.current(), getStatusRef.current()]).then(
      (results) => {
        if (results.every((result) => result.status === "rejected")) {
          toast.error("Unable to load bank account overview.");
        }
      },
    );
  }, []);

  const accounts = useMemo(
    () => buildAccounts(Array.isArray(bankOverview) ? bankOverview : []),
    [bankOverview],
  );
  const normalizedBankAccountStatus = normalizeBslStatus(bankAccountStatus);
  const isBslRejected = normalizedBankAccountStatus === BSL_STATUS.rejected;
  const isBslPendingApproval =
    normalizedBankAccountStatus === BSL_STATUS.pending;
  const isBslApplyIban = normalizedBankAccountStatus === BSL_STATUS.apply;
  const isBslApproved = normalizedBankAccountStatus === BSL_STATUS.approved;
  const shouldShowBslOverview =
    isBslApproved || isBslPendingApproval || isBslApplyIban;
  const bslStateContent = isBslPendingApproval
    ? BSL_PENDING_APPROVAL_STATE
    : isBslApplyIban
      ? BSL_APPLY_IBAN_STATE
      : null;

  const handleDownloadCertificate = useCallback(async () => {
    try {
      const response = await getCertificateRef.current();
      const pdfUrl = response?.Message;

      if (!pdfUrl) return;

      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error("Unable to open the IBAN certificate.");
    }
  }, []);
  const wrapPendingApproval = (content, key) => {
    if (!bslStateContent) return content;

    return (
      <PendingApprovalState
        action={isBslApplyIban ? <ApplyForIbanButton /> : null}
        key={key}
        className="new-bank-overview__pending-state"
        icon={bslStateContent.icon}
        message={bslStateContent.message}
        title={bslStateContent.title}
      >
        {content}
      </PendingApprovalState>
    );
  };

  return (
    <section className="new-bank-overview">
      {shouldShowBslOverview && !isBslRejected ? (
        <>
          <div className="new-bank-overview__heading">
            <h1>BSL Bank Overview</h1>
          </div>

          <div className="new-bank-overview__grid">
            {accounts.map((account) => (
              <div
                className="new-bank-overview__account-column"
                key={account.currency}
              >
                {wrapPendingApproval(
                  <BalanceCard
                    title={account.title}
                    currency={account.currency}
                    total={account.total}
                    available={account.available}
                    book={account.book}
                    held={account.held}
                    connectionState={isBslApproved ? account.connectionState : undefined}
                    lastBalanceDate={isBslApproved ? account.lastBalanceDate : undefined}
                  />,
                  `${account.currency}-balance`,
                )}
                {wrapPendingApproval(
                  <BankDetailsCard
                    iban={account.iban}
                    bicSwift={account.bicSwift}
                    onDownloadCertificate={handleDownloadCertificate}
                  />,
                  `${account.currency}-details`,
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
